import React from 'react';

const SCANNER_COLOR = {
  'zap-baseline': 'var(--info)',
  nuclei: 'var(--warn)',
  testssl: 'var(--ok)',
};

function linePath(values, w, h, max) {
  const n = values.length;
  if (n < 2) return '';
  return values.map((v, i) => {
    const x = (i / (n - 1)) * w;
    const y = h - 4 - (Math.max(0, Number(v) || 0) / max) * (h - 12);
    return `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
}

// Measured series, baseline, floor, and flagged buckets. SVG only —
// the same token colors as the rest of the console.
export function SignalChart({
  label, values = [], baseline = [], floor = null, flagIdx = [], format = (v) => v, hint = '',
}) {
  const w = 640;
  const h = 88;
  const nums = values.map((v) => Number(v) || 0);
  const base = baseline.length === nums.length ? baseline.map((v) => Number(v) || 0) : [];
  const max = Math.max(1e-6, ...nums, ...base, floor || 0);
  const area = linePath(nums, w, h, max);
  const baseLine = base.length ? linePath(base, w, h, max) : '';
  const floorY = floor == null ? null : h - 4 - (Math.max(0, floor) / max) * (h - 12);
  const last = nums.length ? nums[nums.length - 1] : null;
  return (
    <figure className="live-signal">
      <figcaption>
        <span>{label}</span>
        <strong>{last == null ? '—' : format(last)}</strong>
      </figcaption>
      {hint ? <p className="cs-help">{hint}</p> : null}
      <svg viewBox={`0 0 ${w} ${h}`} className="live-signal-svg" role="img" aria-label={label}>
        {floorY != null && <line x1="0" x2={w} y1={floorY} y2={floorY} className="live-floor" />}
        {baseLine && <path d={baseLine} className="live-base" fill="none" />}
        {area && <path d={`${area} L${w},${h} L0,${h} Z`} className="live-area" />}
        {area && <path d={area} className="live-line" fill="none" />}
        {flagIdx.map((i) => {
          if (i < 0 || i >= nums.length) return null;
          const x = nums.length < 2 ? w / 2 : (i / (nums.length - 1)) * w;
          const y = h - 4 - (nums[i] / max) * (h - 12);
          return <circle key={i} cx={x} cy={y} r="3.2" className="live-flag" />;
        })}
      </svg>
    </figure>
  );
}

export function RunChart({ jobs = [] }) {
  const rows = [...jobs].reverse();
  const totals = rows.map((j) => Object.values(j.summary?.scanners || {}).reduce((n, c) => n + (Number(c) || 0), 0));
  const max = Math.max(1, ...totals);
  if (!rows.length) return <p className="cs-help">No scans yet. The bars fill in after the first baseline finishes.</p>;
  return (
    <div className="live-runs" role="img" aria-label="Findings kept on each scan">
      {rows.map((j, i) => {
        const parts = Object.entries(j.summary?.scanners || {}).filter(([, c]) => Number(c) > 0);
        const total = totals[i];
        const when = new Date(j.finishedAt || j.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        return (
          <div key={j.id} className="live-run" title={`${when} · ${total} findings kept`}>
            <div className="live-run-track">
              <div className="live-run-bar" style={{ height: `${Math.max(total ? 8 : 3, (total / max) * 100)}%` }}>
                {parts.length ? parts.map(([k, c]) => (
                  <i key={k} style={{ flexGrow: Number(c) || 0, background: SCANNER_COLOR[k] || 'var(--muted)' }} />
                )) : <i style={{ flexGrow: 1, background: 'var(--line)' }} />}
              </div>
            </div>
            <span>{when}</span>
          </div>
        );
      })}
    </div>
  );
}

export function RunLegend() {
  return (
    <p className="live-legend">
      <i style={{ background: SCANNER_COLOR['zap-baseline'] }} /> ZAP
      <i style={{ background: SCANNER_COLOR.nuclei }} /> Nuclei
      <i style={{ background: SCANNER_COLOR.testssl }} /> TLS
      <span>height is findings kept on that run</span>
    </p>
  );
}
